import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { api } from '../api';
import { Trophy } from '@pxlkit/gamification';
import { Brand, GameIcon, PixelIcon, Shell } from '../components';
import { awardIcons } from '../pixelIcons';
import { awardStepMeta, teamAccentColor } from '../../shared/constants';
import type { AwardStep } from '../../shared/constants';
import ghostUrl from '../assets/ghost.png';

interface DisplayPlayer {
  id: string;
  nickname: string;
  avatar: string;
  teamId?: string | null;
  teamColor?: string | null;
}

interface DisplayTeam {
  id: string;
  name: string;
  color: string;
  icon: string;
  /** Omitted by the server while scores are hidden. */
  score?: number;
}

interface DisplayPhoto {
  id: string;
  teamName?: string | null;
  teamColor?: string | null;
  teamIcon?: string | null;
}

type AwardEntry =
  | { kind: 'team'; id: string; name: string; color: string; icon: string; score: number }
  | {
      kind: 'individual';
      id: string;
      nickname: string;
      avatar: string;
      teamName: string | null;
      teamColor: string | null;
      score: number;
    };

interface DisplayState {
  mode: 'leaderboard' | 'ended' | 'award';
  awardStep: AwardStep | null;
  entry: AwardEntry | null;
}

interface DisplayData {
  game: any;
  teams: DisplayTeam[];
  players: DisplayPlayer[];
  photos: DisplayPhoto[];
  announcement: { message?: string; expiresAt?: string; showOnDisplay?: boolean } | null;
  displayState?: DisplayState;
}

type DriftDirection = 'ltr' | 'rtl' | 'ttb' | 'btt';

interface DriftStyle {
  direction: DriftDirection;
  duration: number;
  delay: number;
  cross: number;
  drift: number;
  scale: number;
  opacity: number;
  rotate: number;
  rotateEnd: number;
  background: string;
  border: string;
  textColor: string;
  blur: number;
}

type DriftChip =
  | (DriftStyle & {
      key: string;
      kind: 'player';
      player: DisplayPlayer;
    })
  | (DriftStyle & {
      key: string;
      kind: 'photo';
      photo: DisplayPhoto;
    });

const POLL_VISIBLE_MS = 5000;
const POLL_HIDDEN_MS = 20000;
const JOINING_STATUSES = ['setup', 'registration'];

function teamStyle(color: string): React.CSSProperties {
  return {
    '--team-color': color,
    '--team-accent': teamAccentColor(color),
  } as React.CSSProperties;
}

function hashString(value: string) {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function podiumShakeVariant(teamId: string) {
  return (hashString(teamId) % 4) + 1;
}

function podiumShakeVars(teamId: string): React.CSSProperties {
  const hash = hashString(`shake:${teamId}`);
  const delay = ((hash % 45) / 10).toFixed(1);
  const duration = (5.2 + ((hash >>> 6) % 28) / 10).toFixed(1);
  return {
    '--shake-delay': `${delay}s`,
    '--shake-duration': `${duration}s`,
  } as React.CSSProperties;
}

function boolish(value: unknown) {
  return value === true || value === 1 || value === '1';
}

function randomBetween(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function pick<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

// Screen-print inks: flat crypt panels with a bone, fire, moon, or ice edge.
const driftPalettes = [
  { background: '#171311', border: '#f1ecdf', text: '#f1ecdf' },
  { background: '#171311', border: '#ff4b1f', text: '#f1ecdf' },
  { background: '#171311', border: '#dbe23c', text: '#f1ecdf' },
  { background: '#171311', border: '#95c0ef', text: '#f1ecdf' },
  { background: '#ff4b1f', border: '#0b0908', text: '#0b0908' },
  { background: '#dbe23c', border: '#0b0908', text: '#0b0908' },
] as const;

function createDriftStyle(teamColor?: string | null): DriftStyle {
  const teamAccent = teamAccentColor(teamColor ?? '#7c3aed');
  const useTeamColor = Math.random() < 0.35;
  const palette = pick(driftPalettes);
  return {
    direction: pick(['ltr', 'rtl', 'ttb', 'btt'] as const),
    duration: randomBetween(14, 28),
    delay: randomBetween(0, 2.5),
    cross: randomBetween(8, 82),
    drift: randomBetween(-12, 12),
    scale: randomBetween(0.7, 1.8),
    opacity: randomBetween(0.28, 0.7),
    rotate: randomBetween(-32, 32),
    rotateEnd: randomBetween(-42, 42),
    background: useTeamColor ? '#171311' : palette.background,
    border: useTeamColor ? teamAccent : palette.border,
    textColor: useTeamColor ? '#f1ecdf' : palette.text,
    blur: randomBetween(0, 1.4),
  };
}

function createPlayerChip(player: DisplayPlayer, key: string): DriftChip {
  return {
    key,
    kind: 'player',
    player,
    ...createDriftStyle(player.teamColor),
  };
}

function createPhotoChip(photo: DisplayPhoto, key: string): DriftChip {
  return {
    key,
    kind: 'photo',
    photo,
    ...createDriftStyle(photo.teamColor),
    // Photos read better a bit larger.
    scale: randomBetween(0.85, 1.7),
  };
}

function displayFingerprint(data: DisplayData) {
  return JSON.stringify({
    name: data.game?.name,
    status: data.game?.status,
    visible: data.game?.leaderboardVisible,
    solo: data.game?.soloMode,
    announcement: data.announcement,
    displayState: data.displayState ?? null,
    teams: (data.teams ?? []).map((team) => [
      team.id,
      team.name,
      team.color,
      team.icon,
      team.score,
    ]),
    players: (data.players ?? []).map((player) => [
      player.id,
      player.nickname,
      player.avatar,
      player.teamId ?? null,
      player.teamColor ?? null,
    ]),
    photos: (data.photos ?? []).map((photo) => [
      photo.id,
      photo.teamName ?? null,
      photo.teamColor ?? null,
      photo.teamIcon ?? null,
    ]),
  });
}

function parseExpiry(value: string) {
  if (value.includes('T') || value.endsWith('Z')) {
    return new Date(value.endsWith('Z') ? value : `${value}Z`).getTime();
  }
  // SQLite CURRENT_TIMESTAMP style: "YYYY-MM-DD HH:MM:SS" (UTC)
  return new Date(`${value.trim().replace(' ', 'T')}Z`).getTime();
}

function isExpired(expiresAt?: string | null) {
  if (!expiresAt) return false;
  return parseExpiry(expiresAt) <= Date.now();
}

export function DisplayPage() {
  const { code } = useParams();
  const [data, setData] = useState<DisplayData | null>(null);
  const fingerprintRef = useRef('');

  useEffect(() => {
    let cancelled = false;
    let pollTimer: number | undefined;
    let expiryTimer: number | undefined;

    async function load() {
      if (!code) return;
      try {
        const next = await api<DisplayData>(`/api/public/display/${code}`);
        if (cancelled) return;
        const fingerprint = displayFingerprint(next);
        if (fingerprint !== fingerprintRef.current) {
          fingerprintRef.current = fingerprint;
          setData(next);
        }
        window.clearTimeout(expiryTimer);
        const expiresAt = next.announcement?.expiresAt;
        if (expiresAt) {
          const wait = Math.max(400, parseExpiry(expiresAt) - Date.now() + 300);
          expiryTimer = window.setTimeout(() => {
            void load();
          }, wait);
        }
      } catch (error) {
        console.warn('Display refresh failed', error);
      }
    }

    function schedule() {
      window.clearTimeout(pollTimer);
      const delay = document.hidden ? POLL_HIDDEN_MS : POLL_VISIBLE_MS;
      pollTimer = window.setTimeout(async () => {
        await load();
        if (!cancelled) schedule();
      }, delay);
    }

    void load().then(() => {
      if (!cancelled) schedule();
    });

    function onVisibility() {
      if (!document.hidden) void load();
      schedule();
    }

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      window.clearTimeout(pollTimer);
      window.clearTimeout(expiryTimer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [code]);

  if (!data)
    return (
      <Shell>
        <div className="center">Opening the Rave Cave…</div>
      </Shell>
    );

  const bannerMessage =
    data.announcement?.message &&
    boolish(data.announcement.showOnDisplay) &&
    !isExpired(data.announcement.expiresAt)
      ? data.announcement.message
      : null;
  const displayState: DisplayState = data.displayState ?? {
    mode: 'leaderboard',
    awardStep: null,
    entry: null,
  };
  const joining = JOINING_STATUSES.includes(data.game.status);

  return (
    <Shell className="display-shell">
      <div className="display-atmosphere grain" aria-hidden>
        <div className="display-moon" />
        <img className="display-ghost" src={ghostUrl} alt="" />
      </div>
      <DriftLayer players={data.players ?? []} photos={data.photos ?? []} />
      <AnnouncementBanner
        message={bannerMessage}
        expiresAt={data.announcement?.expiresAt}
        onExpired={() =>
          setData((current) =>
            current
              ? {
                  ...current,
                  announcement: null,
                }
              : current,
          )
        }
      />
      <header className="display-header">
        <Brand compact />
        <div>
          <span className="live-dot">
            {joining
              ? 'JOIN NOW'
              : displayState.mode === 'ended'
                ? 'GAME OVER'
                : displayState.mode === 'award'
                  ? 'AWARDS'
                  : 'LIVE'}
          </span>
          <strong>{data.game.name}</strong>
        </div>
      </header>
      <div className="display-content">
        {joining ? (
          <JoinQr code={code ?? ''} />
        ) : displayState.mode === 'ended' ? (
          <div className="takeover display-hidden-in">
            <PixelIcon icon={Trophy} />
            <h1>That&rsquo;s a Wrap!</h1>
            <p>Thanks for playing. Awards are coming up next.</p>
          </div>
        ) : displayState.mode === 'award' && displayState.awardStep ? (
          <AwardReveal step={displayState.awardStep} entry={displayState.entry} />
        ) : data.game.soloMode ? (
          data.game.leaderboardVisible ? (
            <>
              <h1 className="display-title">Leaderboard</h1>
              <PlayerLeaderboard players={data.teams ?? []} />
            </>
          ) : (
            <>
              <h1 className="display-title">Who&rsquo;s Playing</h1>
              <p className="display-subtitle">Scores stay secret until the awards.</p>
              <SoloRoster players={data.players ?? []} />
            </>
          )
        ) : (
          <>
            {data.game.leaderboardVisible ? (
              <>
                <h1 className="display-title">Team Leaderboard</h1>
                <LeaderboardPodium teams={data.teams ?? []} />
              </>
            ) : (
              <>
                <h1 className="display-title">The Teams</h1>
                <p className="display-subtitle">Scores stay secret until the awards.</p>
                <TeamRoster teams={data.teams ?? []} players={data.players ?? []} />
              </>
            )}
          </>
        )}
      </div>
    </Shell>
  );
}

function JoinQr({ code }: { code: string }) {
  const url = `${location.origin}/join/${code}`;
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, { width: 800, margin: 2, color: { dark: '#120b1c', light: '#ffffff' } })
      .then((next) => {
        if (!cancelled) setDataUrl(next);
      })
      .catch((error) => console.warn('Join QR failed', error));
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <div className="display-join display-hidden-in">
      <h1 className="display-title">Scan to Join</h1>
      <div className="display-join-qr">
        {dataUrl ? <img src={dataUrl} alt={`QR code to join game ${code}`} /> : null}
      </div>
      <p className="display-join-code">
        Game code <strong>{code.toUpperCase()}</strong>
      </p>
      <small className="display-join-url">{url}</small>
    </div>
  );
}

function AwardReveal({ step, entry }: { step: AwardStep; entry: AwardEntry | null }) {
  const meta = awardStepMeta[step];
  if (!entry) {
    return (
      <div className="takeover display-hidden-in">
        <PixelIcon icon={awardIcons[step].icon} tint={awardIcons[step].tint} />
        <h1>{meta.title}</h1>
        <p>Coming up next…</p>
      </div>
    );
  }
  const isIndividual = entry.kind === 'individual';
  return (
    <div
      className={`award-reveal award-reveal--${step}`}
      style={isIndividual ? undefined : teamStyle(entry.color)}
    >
      <span className="award-reveal-medal" aria-hidden>
        <PixelIcon icon={awardIcons[step].icon} tint={awardIcons[step].tint} />
      </span>
      <h1 className="award-reveal-title">{meta.title}</h1>
      <div className="award-reveal-icon" aria-hidden>
        <GameIcon name={isIndividual ? entry.avatar : entry.icon} />
      </div>
      <h2 className="award-reveal-name">{isIndividual ? entry.nickname : entry.name}</h2>
      {isIndividual && entry.teamName ? (
        <p className="award-reveal-team">{entry.teamName}</p>
      ) : null}
      <AnimatedScore value={Number(entry.score) || 0} />
    </div>
  );
}

function AnnouncementBanner({
  message,
  expiresAt,
  onExpired,
}: {
  message: string | null;
  expiresAt?: string | null;
  onExpired?: () => void;
}) {
  const [visible, setVisible] = useState<string | null>(message);
  const [phase, setPhase] = useState<'in' | 'out' | 'idle'>(message ? 'in' : 'idle');

  useEffect(() => {
    if (message === visible) return;
    if (!message && visible) {
      setPhase('out');
      const timer = window.setTimeout(() => {
        setVisible(null);
        setPhase('idle');
      }, 420);
      return () => window.clearTimeout(timer);
    }
    setVisible(message);
    setPhase(message ? 'in' : 'idle');
  }, [message, visible]);

  useEffect(() => {
    if (!visible || !expiresAt) return;
    const wait = parseExpiry(expiresAt) - Date.now();
    if (wait <= 0) {
      setPhase('out');
      const timer = window.setTimeout(() => {
        setVisible(null);
        setPhase('idle');
        onExpired?.();
      }, 420);
      return () => window.clearTimeout(timer);
    }
    const timer = window.setTimeout(() => {
      setPhase('out');
      window.setTimeout(() => {
        setVisible(null);
        setPhase('idle');
        onExpired?.();
      }, 420);
    }, wait);
    return () => window.clearTimeout(timer);
  }, [visible, expiresAt, onExpired]);

  if (!visible) return null;
  return (
    <div
      className={`display-announcement-banner ${
        phase === 'out' ? 'display-announce-out' : 'display-announce-in'
      }`}
    >
      {visible}
    </div>
  );
}

/** Shown while scores are hidden: every team and its members, with no score or rank. */
function TeamRoster({ teams, players }: { teams: DisplayTeam[]; players: DisplayPlayer[] }) {
  if (teams.length === 0) {
    return <p className="display-subtitle">Teams will appear here once they are set.</p>;
  }
  return (
    <div className="team-roster-board display-hidden-in">
      {teams.map((team) => {
        const members = players.filter((player) => player.teamId === team.id);
        return (
          <section key={team.id} className="roster-team" style={teamStyle(team.color)}>
            <header>
              <span className="roster-team-icon">
                <GameIcon name={team.icon} />
              </span>
              <h2>{team.name}</h2>
              <span className="roster-team-score" aria-label="Score hidden">
                ???
              </span>
            </header>
            {members.length ? (
              <ul>
                {members.map((member) => (
                  <li key={member.id}>
                    <GameIcon name={member.avatar} />
                    <span>{member.nickname}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No members yet</p>
            )}
          </section>
        );
      })}
    </div>
  );
}

/** Ranks past the podium that fit on a TV; the rest are summarised. */
const SOLO_LIST_LIMIT = 16;

/**
 * Solo games: each player's own team carries their nickname, avatar, and score, so the
 * podium shows the top three and a ranked list follows.
 */
function PlayerLeaderboard({ players }: { players: DisplayTeam[] }) {
  if (players.length === 0)
    return <p className="display-subtitle">Players will appear here once they join.</p>;
  const rest = players.slice(3, 3 + SOLO_LIST_LIMIT);
  const hidden = players.length - 3 - rest.length;
  return (
    <div className="solo-leaderboard display-hidden-in">
      <LeaderboardPodium teams={players} size={3} />
      {rest.length ? (
        <ol className="solo-rank-list" start={4}>
          {rest.map((player) => (
            <li key={player.id} style={teamStyle(player.color)}>
              <span className="solo-rank-avatar">
                <GameIcon name={player.icon} />
              </span>
              <span className="solo-rank-name">{player.name}</span>
              <AnimatedScore value={Number(player.score) || 0} />
            </li>
          ))}
        </ol>
      ) : null}
      {hidden > 0 ? <p className="display-subtitle">+{hidden} more playing</p> : null}
    </div>
  );
}

/** Solo games while scores are hidden: just who is here. */
function SoloRoster({ players }: { players: DisplayPlayer[] }) {
  if (players.length === 0)
    return <p className="display-subtitle">Players will appear here once they join.</p>;
  return (
    <ul className="solo-roster display-hidden-in">
      {players.map((player) => (
        <li key={player.id} style={teamStyle(player.teamColor ?? '#7c3aed')}>
          <GameIcon name={player.avatar} />
          <span>{player.nickname}</span>
        </li>
      ))}
    </ul>
  );
}

function LeaderboardPodium({ teams, size = 4 }: { teams: DisplayTeam[]; size?: number }) {
  const top = teams.slice(0, size);
  const nodeRefs = useRef(new Map<string, HTMLDivElement>());
  const prevRects = useRef(new Map<string, DOMRect>());
  const signature = top
    .map((team) => `${team.id}:${team.score}:${team.name}:${team.icon}`)
    .join('|');

  useLayoutEffect(() => {
    const nextRects = new Map<string, DOMRect>();
    for (const team of top) {
      const el = nodeRefs.current.get(team.id);
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      nextRects.set(team.id, rect);
      const prev = prevRects.current.get(team.id);
      if (prev) {
        const dx = prev.left - rect.left;
        const dy = prev.top - rect.top;
        if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
          el.animate(
            [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }],
            { duration: 750, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
          );
        }
      } else if (prevRects.current.size > 0) {
        el.animate(
          [
            { opacity: 0, transform: 'translateY(28px) scale(0.96)' },
            { opacity: 1, transform: 'translateY(0) scale(1)' },
          ],
          { duration: 560, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
        );
      }
    }
    prevRects.current = nextRects;
  }, [signature]);

  return (
    <div className="podium">
      {top.map((team, index) => (
        <div
          key={team.id}
          ref={(node) => {
            if (node) nodeRefs.current.set(team.id, node);
            else nodeRefs.current.delete(team.id);
          }}
          className={`podium-card place-${index + 1}`}
          style={
            {
              ...teamStyle(team.color),
              '--podium-delay': `${[0, 1.35, 0.7, 2.1][index] ?? index * 0.8}s`,
              ...podiumShakeVars(team.id),
            } as React.CSSProperties
          }
        >
          <div className={`podium-card-shake podium-shake-${podiumShakeVariant(team.id)}`}>
            <div className="podium-card-motion">
              <span className="place">{index + 1}</span>
              <div>
                <GameIcon name={team.icon} />
              </div>
              <h2>{team.name}</h2>
              <AnimatedScore value={Number(team.score) || 0} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AnimatedScore({ value }: { value: number }) {
  const [display, setDisplay] = useState(value);
  const [bump, setBump] = useState(false);
  const previous = useRef(value);

  useEffect(() => {
    const from = previous.current;
    previous.current = value;
    if (from === value) {
      setDisplay(value);
      return;
    }
    setBump(true);
    const bumpTimer = window.setTimeout(() => setBump(false), 700);
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setDisplay(value);
      return () => window.clearTimeout(bumpTimer);
    }
    const delta = value - from;
    const steps = Math.min(24, Math.max(10, Math.abs(delta)));
    let frame = 0;
    const timer = window.setInterval(() => {
      frame += 1;
      const progress = frame / steps;
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + delta * eased));
      if (frame >= steps) window.clearInterval(timer);
    }, 28);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(bumpTimer);
    };
  }, [value]);

  return <strong className={bump ? 'score-bump' : undefined}>{display.toLocaleString()}</strong>;
}

function DriftLayer({ players, photos }: { players: DisplayPlayer[]; photos: DisplayPhoto[] }) {
  const id = useId();
  const seq = useRef(0);
  const playersRef = useRef(players);
  const photosRef = useRef(photos);
  const [chips, setChips] = useState<DriftChip[]>([]);
  const rosterKey = [
    ...players.map((player) => `p:${player.id}`),
    ...photos.map((photo) => `m:${photo.id}`),
  ]
    .sort()
    .join('|');
  const reducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    playersRef.current = players;
    photosRef.current = photos;
  }, [players, photos]);

  useEffect(() => {
    if (!rosterKey || reducedMotion) {
      setChips([]);
      return;
    }

    const totalItems = () => playersRef.current.length + photosRef.current.length;
    const maxConcurrent = Math.min(10, Math.max(3, totalItems()));

    function spawn() {
      const playerList = playersRef.current;
      const photoList = photosRef.current;
      if (!playerList.length && !photoList.length) return;

      setChips((current) => {
        if (current.length >= maxConcurrent) return current;
        seq.current += 1;
        const key = `${id}-${seq.current}`;
        const preferPhoto =
          photoList.length > 0 &&
          (playerList.length === 0 || Math.random() < (photoList.length > 2 ? 0.45 : 0.3));
        const next = preferPhoto
          ? createPhotoChip(pick(photoList), key)
          : createPlayerChip(pick(playerList), key);
        return [...current, next];
      });
    }

    const starters: number[] = [];
    for (let i = 0; i < Math.min(5, maxConcurrent); i += 1) {
      starters.push(window.setTimeout(spawn, i * 800));
    }

    const timer = window.setInterval(() => {
      if (Math.random() > 0.3) spawn();
    }, 2000);

    return () => {
      starters.forEach((handle) => window.clearTimeout(handle));
      window.clearInterval(timer);
    };
  }, [rosterKey, id, reducedMotion]);

  if (reducedMotion || (!players.length && !photos.length)) return null;

  return (
    <div className="display-drift-layer" aria-hidden>
      {chips.map((chip) => (
        <div
          key={chip.key}
          className={`display-drift-chip display-drift-chip--${chip.kind} display-drift-chip--${chip.direction}`}
          style={
            {
              '--drift-duration': `${chip.duration}s`,
              '--drift-delay': `${chip.delay}s`,
              '--drift-cross': `${chip.cross}%`,
              '--drift-shift': `${chip.drift}vh`,
              '--drift-scale': chip.scale,
              '--drift-opacity': chip.opacity,
              '--drift-rotate': `${chip.rotate}deg`,
              '--drift-rotate-end': `${chip.rotateEnd}deg`,
              '--drift-bg': chip.background,
              '--drift-border': chip.border,
              '--drift-text': chip.textColor,
              '--drift-blur': `${chip.blur}px`,
            } as React.CSSProperties
          }
          onAnimationEnd={() =>
            setChips((current) => current.filter((entry) => entry.key !== chip.key))
          }
        >
          {chip.kind === 'player' ? (
            <>
              <span className="display-drift-avatar">
                <GameIcon name={chip.player.avatar} />
              </span>
              <span className="display-drift-name">{chip.player.nickname}</span>
            </>
          ) : (
            <>
              <img
                className="display-drift-photo"
                src={`/media/${chip.photo.id}`}
                alt=""
                loading="lazy"
              />
              {chip.photo.teamName ? (
                <span className="display-drift-name">
                  <span className="display-drift-team-icon" aria-hidden>
                    <GameIcon name={chip.photo.teamIcon} />
                  </span>
                  {chip.photo.teamName}
                </span>
              ) : null}
            </>
          )}
        </div>
      ))}
    </div>
  );
}
